import { useEffect, useMemo, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { QueryClientProvider } from '@tanstack/react-query'
import { prefixer } from 'stylis'
import rtlPlugin from 'stylis-plugin-rtl'
import { useTranslation } from 'react-i18next'

import { buildTheme } from './theme'
import { queryClient } from './api/queryClient'
import { isRtl } from './i18n'
import { AuthProvider, useAuth } from './store/AuthContext'
import { LedgerCacheProvider } from './store/LedgerCache'
import { HotkeyProvider } from './hotkeys/HotkeyProvider'
import { ThemeModeProvider, useThemeMode } from './store/ThemeMode'
import AppLayout from './components/AppLayout'
import LoadingScreen from './components/LoadingScreen'
import LoginPage from './pages/Login'
import VanApp from './van/VanApp'
import VanPhones from './inventory/VanPhones'
import HomePage from './pages/Home'
import ReportsPage from './pages/Reports'
import ChartOfAccounts from './admin/ChartOfAccounts'
import Settings from './admin/Settings'
import Fleet from './transport/Fleet'
import TripEntry from './transport/TripEntry'
import TripInvoicing from './transport/TripInvoicing'
import TripRegister from './transport/TripRegister'
import { VehicleProfitPage } from './transport/VehicleProfit'
import VoucherEntry from './vouchers/VoucherEntry'
import RequireModule from './components/RequireModule'
import ItemsPage from './inventory/Items'
import StoresPage from './inventory/Stores'
import OpeningStockPage from './inventory/OpeningStock'
import PurchasesPage from './inventory/Purchases'
import PurchaseEntry from './inventory/PurchaseEntry'
import PurchaseView from './inventory/PurchaseView'
import SalesPage from './inventory/Sales'
import SaleEntry from './inventory/SaleEntry'
import SaleView from './inventory/SaleView'
import TransfersPage from './inventory/Transfers'
import TransferEntry from './inventory/TransferEntry'
import TransferView from './inventory/TransferView'
import CountEntry from './inventory/CountEntry'
import CountView from './inventory/CountView'
import {
  LowStockPage, SalesByItemPage, StockCardPage, StockOnHandPage, VanStockPage,
} from './inventory/StockReports'

const ltrCache = createCache({ key: 'mui', stylisPlugins: [prefixer] })
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] })

function Shell() {
  const { isAuthenticated, loading, me } = useAuth()
  const { pathname } = useLocation()
  const { bindUser } = useThemeMode()
  const username = isAuthenticated ? me?.username ?? null : null
  useEffect(() => { bindUser(username) }, [bindUser, username])

  // The van app answers for itself: once a phone is set up it bills with
  // no sign-in and no signal, so it must not wait on either.
  if (pathname === '/van' || pathname.startsWith('/van/')) {
    return loading ? <LoadingScreen /> : (
      <Routes><Route path="/van/*" element={<VanApp />} /></Routes>)
  }
  if (loading) return <LoadingScreen />
  if (!isAuthenticated || !me) return <LoginPage />
  /*
   * Keyed on the company. Switching company must not leave one company's
   * figures on screen while another's load -- in an accounting app that is
   * not a cosmetic glitch, it is a wrong number shown as a right one.
   * The key tears the whole authenticated tree down, which empties the
   * ledger cache with it; AuthContext empties the query cache in step.
   */
  return (
    <LedgerCacheProvider key={me.company_id}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/daybook" element={<ReportsPage which="daybook" />} />
          <Route path="/outstanding" element={<ReportsPage which="outstanding" />} />
          <Route path="/ledger" element={<ReportsPage which="ledger" />} />
          <Route path="/trial-balance" element={<ReportsPage which="trial-balance" />} />
          <Route path="/balance-sheet" element={<ReportsPage which="balance-sheet" />} />
          <Route path="/profit-loss" element={<ReportsPage which="profit-loss" />} />
          <Route path="/sales-register"
            element={<ReportsPage which="sales-register" />} />
          <Route path="/purchase-register"
            element={<ReportsPage which="purchase-register" />} />
          <Route path="/cash-book" element={<ReportsPage which="cash-book" />} />
          <Route path="/bank-book" element={<ReportsPage which="bank-book" />} />
          <Route path="/vat-return" element={<ReportsPage which="vat-return" />} />
          <Route path="/transport/profitability" element={<RequireModule name="transport"><VehicleProfitPage /></RequireModule>} />
          <Route path="/accounts" element={<ChartOfAccounts />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/transport/trips" element={<RequireModule name="transport"><TripEntry /></RequireModule>} />
          <Route path="/transport/invoicing" element={<RequireModule name="transport"><TripInvoicing /></RequireModule>} />
          <Route path="/transport/fleet" element={<RequireModule name="transport"><Fleet /></RequireModule>} />
          <Route path="/transport/register" element={<RequireModule name="transport"><TripRegister /></RequireModule>} />
          <Route path="/inventory/items"
            element={<RequireModule name="inventory"><ItemsPage /></RequireModule>} />
          <Route path="/inventory/stores"
            element={<RequireModule name="inventory"><StoresPage /></RequireModule>} />
          <Route path="/inventory/opening"
            element={<RequireModule name="inventory"><OpeningStockPage /></RequireModule>} />
          <Route path="/inventory/purchases"
            element={<RequireModule name="inventory"><PurchasesPage /></RequireModule>} />
          <Route path="/inventory/purchases/new"
            element={<RequireModule name="inventory"><PurchaseEntry /></RequireModule>} />
          <Route path="/inventory/purchases/:id"
            element={<RequireModule name="inventory"><PurchaseView /></RequireModule>} />
          <Route path="/inventory/sales"
            element={<RequireModule name="inventory"><SalesPage /></RequireModule>} />
          <Route path="/inventory/sales/new"
            element={<RequireModule name="inventory"><SaleEntry /></RequireModule>} />
          <Route path="/inventory/sales/:id"
            element={<RequireModule name="inventory"><SaleView /></RequireModule>} />
          <Route path="/inventory/transfers"
            element={<RequireModule name="inventory"><TransfersPage /></RequireModule>} />
          <Route path="/inventory/transfers/new"
            element={<RequireModule name="inventory"><TransferEntry /></RequireModule>} />
          <Route path="/inventory/transfers/:id"
            element={<RequireModule name="inventory"><TransferView /></RequireModule>} />
          <Route path="/inventory/counts"
            element={<RequireModule name="inventory"><TransfersPage kind="counts" /></RequireModule>} />
          <Route path="/inventory/counts/new"
            element={<RequireModule name="inventory"><CountEntry /></RequireModule>} />
          <Route path="/inventory/counts/:id"
            element={<RequireModule name="inventory"><CountView /></RequireModule>} />
          <Route path="/inventory/stock"
            element={<RequireModule name="inventory"><StockOnHandPage /></RequireModule>} />
          <Route path="/inventory/stock-card"
            element={<RequireModule name="inventory"><StockCardPage /></RequireModule>} />
          <Route path="/inventory/van-stock"
            element={<RequireModule name="inventory"><VanStockPage /></RequireModule>} />
          <Route path="/inventory/sales-by-item"
            element={<RequireModule name="inventory"><SalesByItemPage /></RequireModule>} />
          <Route path="/inventory/reorder"
            element={<RequireModule name="inventory"><LowStockPage /></RequireModule>} />
          <Route path="/inventory/van-phones"
            element={<RequireModule name="inventory"><VanPhones /></RequireModule>} />
          <Route path="/vouchers/:kind" element={<VoucherEntry />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </LedgerCacheProvider>
  )
}

export default function App() {
  return (
    <ThemeModeProvider>
      <ThemedApp />
    </ThemeModeProvider>
  )
}

function ThemedApp() {
  const { i18n } = useTranslation()
  const { mode } = useThemeMode()
  const [lang, setLang] = useState(i18n.language)

  useEffect(() => {
    const onChange = (l: string) => setLang(l)
    i18n.on('languageChanged', onChange)
    return () => { i18n.off('languageChanged', onChange) }
  }, [i18n])

  const rtl = isRtl(lang)
  const theme = useMemo(() => buildTheme(rtl ? 'rtl' : 'ltr', mode), [rtl, mode])

  useEffect(() => {
    document.documentElement.dir = rtl ? 'rtl' : 'ltr'
    document.documentElement.lang = lang
  }, [rtl, lang])

  return (
    <CacheProvider value={rtl ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <HotkeyProvider>
            <AuthProvider>
              <BrowserRouter>
                <Shell />
              </BrowserRouter>
            </AuthProvider>
          </HotkeyProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </CacheProvider>
  )
}
